import React, { useState, useEffect } from "react";
import {
    Card,
    CardBody,
    CardHeader,
    Row,
    Col,
    Spinner,
    Badge,
    Button,
    Alert,
    Label, NavLink, NavItem, Nav,
} from "reactstrap"
import axios from "axios";
import { useParams } from "react-router-dom";
import { API } from "../../api/urls";
import { ACCOUNT_TYPE_LABEL_MAP } from "../../constants/accountTypes";
import AccountMovementInput from "./AccountMovementInput";
import AccountMovementList from "./PaymentPlanCard";
import AccountDebtReminderTab from "./AccountDebtReminderTab";
import { NetBalanceHero, CurrencyBalanceCards } from "./CurrencyBalanceSummary";
import { fetchFinalRates } from "../../api/requests/payment-requests";
import { formatCurrencyCode } from "../../utils/currencyUtils";
import "../../assets/css/page-common.css";
import BackButton from "../../components/Common/BackButton";

const AccountDetailPage = () => {
    document.title = "Cari Detayları | Tahsilat360";
    const { id: accountId } = useParams();
    const [activeMainTab, setActiveMainTab] = useState("DETAIL");
    const [account, setAccount] = useState(null);
    const [balanceSummary, setBalanceSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [companyName, setCompanyName] = useState("-");
    const [groups, setGroups] = useState([]);
    const [loadingGroups, setLoadingGroups] = useState(false);
    const [finalRates, setFinalRates] = useState(null);

    useEffect(() => {
        if (accountId) {
            fetchAccountDetails(accountId);
        } else {
            setError("Görüntülenecek cari ID'si bulunamadı.");
            setLoading(false);
        }
    }, [accountId]);

    const handleMovementSaved = () => {
        fetchAccountDetails(accountId);
    };

    const fetchAccountDetails = async (id) => {
        setLoading(true);
        setError(null);
        try {

            // 1. Cari Detaylarını Çek
            const accountRes = await axios.get(API.account(id), {
                headers: {},
            });
            const accountData = accountRes.data.data;
            setAccount(accountData);

            // 2. Finansal Özeti Çek
            const summaryRes = await axios.get(API.account_summary(id), {
                headers: {},
            });
            const summaryData = summaryRes.data.data;
            setBalanceSummary(summaryData);

            await fetchCompanyName(accountData.companyId);

            if (accountData.companyId) {
                await fetchGroups(accountData.companyId);
                await loadFinalRates(accountData.companyId);
            }
        } catch (err) {
            console.error("Cari detayları yüklenirken hata:", err.response || err);
            setError(err.response?.data?.message || "Cari detayları yüklenirken bir hata oluştu.");
            setBalanceSummary(null);
        } finally {
            setLoading(false);
        }
    };

    const fetchCompanyName = async (companyId) => {
        if (!companyId) return;
        try {
            const response = await axios.get(API.get_companies_names, {
                headers: {},
            });
            const company = (response.data.data || []).find(c => c.id === companyId);
            setCompanyName(company ? company.companyName : companyId);
        } catch {}
    };

    // Firmanın güncel kurları (TCMB + firma markup'ı, backend'de periyodik güncelleniyor).
    // Yalnızca TL karşılığı gösterimi içindir; alınamazsa ekran kurusuz çalışmaya devam eder.
    const loadFinalRates = async (companyId) => {
        if (!companyId) return;
        try {
            const res = await fetchFinalRates(companyId);
            if (!res.ok) throw new Error(`final-rates ${res.status}`);
            const { data } = await res.json();
            setFinalRates(data || null);
            if (data && !data.usdFinalRate && !data.eurFinalRate && !data.gbpFinalRate) {
                console.warn(
                    "Firmanın güncel kurları tanımlı değil, TL karşılığı gösterilemeyecek. " +
                    "Ayarlar > Döviz Kuru Yapılandırması ekranından kontrol ediniz.",
                    { companyId, data }
                );
            }
        } catch (err) {
            console.warn("Güncel kurlar alınamadı, TL karşılığı gösterilemeyecek:", err);
            setFinalRates(null);
        }
    };

    const fetchGroups = async (companyId) => {
        setLoadingGroups(true);
        try {
            const res = await axios.get(API.get_account_groups_by_company(companyId), {
                headers: {},
            });
            setGroups(res.data.data || []);
        } catch {
            setGroups([]);
        } finally {
            setLoadingGroups(false);
        }
    };

    const getGroupName = (groupId) => {
        const group = groups.find(g => g.id === groupId);
        return group ? group.groupName : groupId;
    };

    const formatDate = (dateString) => {
        if (!dateString) return "-";
        return new Date(dateString).toLocaleDateString("tr-TR", {
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit"
        });
    };

    const getAccountTypeBadgeColor = (accountType) => {
        switch (accountType) {
            case "SOLE_PROPRIETORSHIP": return "success";
            case "JOINT_STOCK_COMPANY": return "info";
            case "LIMITED_COMPANY": return "primary";
            case "COOPERATIVE": return "warning";
            case "GENERAL_PARTNERSHIP": return "secondary";
            case "COMMANDITE_COMPANY": return "dark";
            default: return "secondary";
        }
    };

    const translateAccountType = (accountType) => {
        switch (accountType) {
            case "SOLE_PROPRIETORSHIP": return "Şahıs Şirketi";
            case "JOINT_STOCK_COMPANY": return "Anonim Şirket";
            case "LIMITED_COMPANY": return "Limited Şirket";
            case "COOPERATIVE": return "Kooperatif";
            case "GENERAL_PARTNERSHIP": return "Kolektif Şirket";
            case "COMMANDITE_COMPANY": return "Komandit Şirket";
            default: return accountType;
        }
    };

    const formatCurrency = (amount) => formatCurrencyCode(amount, "TRY");

    // Yeni, özlü kart bileşeni (Borç/Tahsilat için)
    const SmallSummaryCard = ({ title, value, color, icon }) => (
        <div className="d-flex align-items-center justify-content-between p-3 rounded-4 bg-white shadow-sm border h-100">
            <div className="flex-grow-1">
                <p className="text-muted small mb-1">{title}</p>
                <h6 className={`fw-bold mb-0 text-${color}`}>
                    {formatCurrency(value || 0)}
                </h6>
            </div>
            <i className={`bx ${icon} fs-4 text-${color}-subtle`}></i>
        </div>
    );

    const summary = balanceSummary; // Kolay erişim için

    // **********************************************
    // TOPLAM BORÇ HESAPLAMASI (MANTIK DÜZELTME)
    // **********************************************
    let calculatedTotalDebt = 0;
    if (summary?.currentBalance != null && summary?.totalReceiptAmount != null) {
        // Borcun pozitif değer olarak gösterilmesi için mutlak değer kullanılır.
        // totalDebt = |currentBalance| + totalReceipt
        const netDebt = Math.abs(summary.currentBalance);
        calculatedTotalDebt = netDebt + summary.totalReceiptAmount;
    }
    // **********************************************

    // Hareketler sekmesindeki geniş tablo için sayfa tam genişliğe geçer.
    const isMovementsTab = activeMainTab === "MOVEMENTS";

    // Döviz bazlı bakiyeler; backend "balances" göndermiyorsa TRY'ye düşülür.
    const balanceCards = (summary?.balances && summary.balances.length > 0)
        ? summary.balances
        : summary
            ? [{
                currency: "TRY",
                currentBalance: summary.currentBalance,
                totalDebtAmount: summary.totalDebtAmount ?? calculatedTotalDebt,
                totalReceiptAmount: summary.totalReceiptAmount
            }]
            : [];

    if (loading) {
        return (
            <div className="page-content page-common text-center p-5">
                <Spinner size="lg" color="primary" />
                <h6 className="mt-3 text-muted">Yükleniyor...</h6>
            </div>
        );
    }

    if (error) {
        return (
            <div className="page-content page-common">
                <div className="container-fluid">
                    <Alert color="danger" className="rounded-4">
                        <i className="bx bx-error-circle me-2" />
                        {error}
                        <button className="btn btn-sm btn-outline-danger ms-2 btn-rounded" onClick={() => fetchAccountDetails(accountId)}>
                            Tekrar Dene
                        </button>
                    </Alert>
                </div>
            </div>
        );
    }

    return (
        <React.Fragment>
            <div className="page-content page-common">
                <div className="container-fluid">

                    {/* ── Page Header ── */}
                    <div className="page-header d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <div className="d-flex align-items-center gap-3">
                            <BackButton />
                            <div>
                                <h5 className="mb-0 fw-bold" style={{ color: "#1e293b" }}>
                                    {account?.accountName || "Cari/Bayi"}
                                </h5>
                                <span className="text-muted" style={{ fontSize: ".82rem" }}>
                                    Cari/Bayi Detay Sayfası
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* ── Net Toplam Bakiye (TL karşılığı) + döviz özetleri ── */}
                    <NetBalanceHero
                        balances={balanceCards}
                        finalRates={finalRates}
                        className="mb-4"
                    />

                    <Row className="g-4">
                        <Col lg={isMovementsTab ? 9 : 7}>
                            <Card className="bg-white border-0 shadow-lg h-100 rounded-4">
                                <CardHeader className="bg-white border-0 pb-0 rounded-top-4">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <h5 className="mb-0 text-dark fw-semibold d-flex align-items-center">
                                            <i className="bx bx-user-detail me-2"></i>
                                            {account.accountName}
                                        </h5>
                                    </div>

                                    <Nav tabs className="account-main-tabs">
                                        <NavItem style={{ marginLeft: '1rem' }}>
                                            <NavLink
                                                className={
                                                    "account-main-tab-link " +
                                                    (activeMainTab === "DETAIL" ? "active navy" : "")
                                                }
                                                onClick={() => setActiveMainTab("DETAIL")}
                                            >
                                                <i className="bx bx-user"></i> Cari Hesabı
                                            </NavLink>
                                        </NavItem>

                                        <NavItem>
                                            <NavLink
                                                className={
                                                    "account-main-tab-link " +
                                                    (activeMainTab === "DEBT_REMINDER" ? "active orange" : "")
                                                }
                                                onClick={() => setActiveMainTab("DEBT_REMINDER")}
                                            >
                                                <i className="bx bx-bell"></i> Borç Hatırlatma
                                            </NavLink>
                                        </NavItem>

                                        <NavItem>
                                            <NavLink
                                                className={
                                                    "account-main-tab-link " +
                                                    (activeMainTab === "MOVEMENTS" ? "active navy" : "")
                                                }
                                                onClick={() => setActiveMainTab("MOVEMENTS")}
                                            >
                                                <i className="bx bx-list-ul"></i> Hareketler
                                            </NavLink>
                                        </NavItem>
                                    </Nav>
                                </CardHeader>

                                <CardBody className={isMovementsTab ? "p-2 pt-3" : "p-4"}>
                                    {activeMainTab === "DETAIL" && (
                                        <>
                                            <div className="mb-4">
                                                <CurrencyBalanceCards
                                                    balances={balanceCards}
                                                    finalRates={finalRates}
                                                />
                                            </div>

                                            <Row className="g-3 mb-4">
                                                <Col md={12}>
                                                    <div className="d-flex flex-column justify-content-center p-3 rounded-4 bg-white shadow-sm border h-100">
                                                        <div>
                                                            <p className="text-muted small mb-1">
                                                                Bağlı Firma
                                                            </p>
                                                            <h6 className="fw-bold mb-0 text-dark">
                                                                {companyName}
                                                            </h6>
                                                            <Badge
                                                                color={getAccountTypeBadgeColor(
                                                                    account.accountType
                                                                )}
                                                                className="mt-1 rounded-pill px-2 py-1"
                                                                style={{
                                                                    fontSize: "0.7rem",
                                                                }}
                                                            >
                                                                {ACCOUNT_TYPE_LABEL_MAP[
                                                                    account.accountType
                                                                    ] || account.accountType}
                                                            </Badge>
                                                        </div>
                                                    </div>
                                                </Col>
                                            </Row>

                                            <div
                                                className="border rounded-4 p-3 mb-3"
                                                style={{ backgroundColor: "#f9f9f9" }}
                                            >
                                                <Row className="g-3">
                                                    <Col md={6}>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <i className="bx bx-id-card text-muted"></i>
                                                            <div>
                                                                <small
                                                                    className="text-muted d-block"
                                                                    style={{
                                                                        fontSize: "0.75rem",
                                                                    }}
                                                                >
                                                                    TC Kimlik / VKN
                                                                </small>
                                                                <span className="fw-medium small">
                                                                    {account.nationalId ||
                                                                        account.vknNumber ||
                                                                        "-"}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                    <Col md={6}>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <i className="bx bx-building text-muted"></i>
                                                            <div>
                                                                <small
                                                                    className="text-muted d-block"
                                                                    style={{
                                                                        fontSize: "0.75rem",
                                                                    }}
                                                                >
                                                                    Vergi Dairesi
                                                                </small>
                                                                <span className="fw-medium small">
                                                                    {account.taxOffice || "-"}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                    <Col md={6}>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <i className="bx bx-phone text-muted"></i>
                                                            <div>
                                                                <small
                                                                    className="text-muted d-block"
                                                                    style={{
                                                                        fontSize: "0.75rem",
                                                                    }}
                                                                >
                                                                    Telefon
                                                                </small>
                                                                <span className="fw-medium small">
                                                                    {account.phoneNumber ? (
                                                                        <a
                                                                            href={`tel:${account.phoneNumber}`}
                                                                            className="text-decoration-none text-dark"
                                                                        >
                                                                            {account.phoneNumber}
                                                                        </a>
                                                                    ) : (
                                                                        "-"
                                                                    )}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                    <Col md={6}>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <i className="bx bx-map text-muted"></i>
                                                            <div>
                                                                <small
                                                                    className="text-muted d-block"
                                                                    style={{
                                                                        fontSize: "0.75rem",
                                                                    }}
                                                                >
                                                                    Konum
                                                                </small>
                                                                <span className="fw-medium small">
                                                                    {[
                                                                        account.city,
                                                                        account.district,
                                                                    ]
                                                                        .filter(Boolean)
                                                                        .join(", ") || "-"}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </div>

                                            {account.address && (
                                                <div
                                                    className="border rounded-4 p-3 mb-3"
                                                    style={{ backgroundColor: "#f9f9f9" }}
                                                >
                                                    <small
                                                        className="text-muted d-block mb-1"
                                                        style={{ fontSize: "0.75rem" }}
                                                    >
                                                        <i className="bx bx-map-pin me-1"></i>
                                                        Adres
                                                    </small>
                                                    <p className="mb-0 small text-dark">
                                                        {account.address}
                                                    </p>
                                                </div>
                                            )}

                                            {account.description && (
                                                <div
                                                    className="border rounded-4 p-3 mb-3"
                                                    style={{ backgroundColor: "#f9f9f9" }}
                                                >
                                                    <small
                                                        className="text-muted d-block mb-1"
                                                        style={{ fontSize: "0.75rem" }}
                                                    >
                                                        <i className="bx bx-note me-1"></i>
                                                        Açıklama
                                                    </small>
                                                    <p className="mb-0 small text-dark">
                                                        {account.description}
                                                    </p>
                                                </div>
                                            )}

                                            <div
                                                className="border rounded-4 p-3 mb-3"
                                                style={{ backgroundColor: "#f9f9f9" }}
                                            >
                                                <small
                                                    className="text-muted d-block mb-2"
                                                    style={{ fontSize: "0.75rem" }}
                                                >
                                                    <i className="bx bx-group me-1"></i>
                                                    Taksit Grupları
                                                </small>
                                                {loadingGroups ? (
                                                    <div className="text-center py-2">
                                                        <Spinner size="sm" />
                                                    </div>
                                                ) : account.accountGroupId ? (
                                                    <Badge className="rounded-pill px-3 py-1" color="secondary">
                                                        {getGroupName(account.accountGroupId)}
                                                    </Badge>
                                                ) : (
                                                    <small className="text-muted">
                                                        Grup atanmamış
                                                    </small>
                                                )}
                                            </div>

                                            <div className="d-flex justify-content-between pt-3 border-top">
                                                <div>
                                                    <small
                                                        className="text-muted d-block"
                                                        style={{ fontSize: "0.7rem" }}
                                                    >
                                                        Oluşturulma
                                                    </small>
                                                    <small className="fw-medium">
                                                        {formatDate(account.createdAt)}
                                                    </small>
                                                </div>
                                                <div className="text-end">
                                                    <small
                                                        className="text-muted d-block"
                                                        style={{ fontSize: "0.7rem" }}
                                                    >
                                                        Son Güncelleme
                                                    </small>
                                                    <small className="fw-medium">
                                                        {formatDate(account.updatedAt)}
                                                    </small>
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    {activeMainTab === "DEBT_REMINDER" && (
                                        <AccountDebtReminderTab
                                            accountId={accountId}
                                            account={account}
                                            balanceSummary={summary}
                                            formatCurrency={formatCurrency}
                                        />
                                    )}

                                    {activeMainTab === "MOVEMENTS" && (
                                        <AccountMovementList
                                            accountId={accountId}
                                            formatCurrency={formatCurrency}
                                            onMovementDeleted={handleMovementSaved}
                                            cardClassName="bg-white border-0 shadow-none"
                                        />
                                    )}
                                </CardBody>
                            </Card>
                        </Col>

                        <Col lg={isMovementsTab ? 3 : 5}>
                            <AccountMovementInput
                                accountId={accountId}
                                onSaved={handleMovementSaved}
                                finalRates={finalRates}
                            />
                        </Col>
                    </Row>
                </div>
            </div>
        </React.Fragment>
    );
};

export default AccountDetailPage;